/**
 * «تطبیق و توقف اضطراری» (Phase 4, FINANCIAL_INVARIANTS §6, FI-16). Shows whether sensitive financial
 * mutation is halted, the latest reconciliation runs with each check, and the kill-switch history.
 * Run now = a read-only check over PostgreSQL truth; an unexplained mismatch halts automatically.
 * HALT is always available to the Full Admin; RELEASE is refused by the server unless the latest run
 * is CLEAN (there is no bypass). Both require reason + step-up and are audited.
 */
import {
  AppContainer,
  Badge,
  Button,
  DataTable,
  formatAmount,
  Ltr,
  PageHeader,
  StateView,
} from '@hopex/ui';
import { useState } from 'react';
import { api, dt, type FinanceOverview, type ReconRuns } from '../../api.ts';
import { CommandDialog, Facts } from '../../lib/command.tsx';
import { RECON_CHECK, RUN_STATUS } from '../../lib/money-labels.ts';
import { Remote, useRemote } from '../../lib/remote.tsx';
import { TechCode } from '../../lib/ui.tsx';
import { Counter, num, Tone } from './shared.tsx';

export function ReconciliationPage() {
  const ov = useRemote<FinanceOverview>('/finance/overview');
  const runs = useRemote<ReconRuns>('/reconciliation/runs');
  const [dialog, setDialog] = useState<'RUN' | 'HALT' | 'RELEASE' | null>(null);
  const reload = () => {
    ov.reload();
    runs.reload();
  };
  return (
    <AppContainer>
      <PageHeader title="تطبیق و توقف اضطراری" />
      <Remote r={ov}>
        {(o) => (
          <div
            className="mb-4 flex flex-col gap-3"
            data-financial-state={o.halted ? 'HALTED' : 'OPEN'}
          >
            <div
              className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4 ${o.halted ? 'border-negative/50 bg-negative-tint' : 'border-line bg-surface-1'}`}
            >
              <div>
                <p className="m-0 text-[15px] font-bold">
                  {o.halted ? 'عملیات مالی حساس متوقف است' : 'عملیات مالی حساس فعال است'}
                </p>
                <p className="m-0 mt-1 text-[12.5px] text-fg-muted">
                  {o.halted
                    ? `از ${dt(o.haltedSince)} · ${o.haltReason ?? ''}`
                    : 'آخرین تطبیق: ' +
                      (o.lastReconciliation
                        ? `${RUN_STATUS[o.lastReconciliation.status]?.label ?? o.lastReconciliation.status} · ${dt(o.lastReconciliation.startedAt)}`
                        : 'هنوز اجرا نشده')}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onPress={() => setDialog('RUN')}
                  data-run-recon
                >
                  اجرای تطبیق
                </Button>
                {o.halted ? (
                  <Button
                    size="sm"
                    variant="positive"
                    onPress={() => setDialog('RELEASE')}
                    data-release-halt
                  >
                    رفع توقف
                  </Button>
                ) : (
                  <Button size="sm" variant="danger" onPress={() => setDialog('HALT')} data-halt>
                    توقف اضطراری
                  </Button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Counter
                label="بدهی به کاربران (تومان)"
                value={
                  <Ltr className="hx-num">
                    {formatAmount(o.userLiabilitiesToman, 'TOMAN', { withUnit: false })}
                  </Ltr>
                }
              />
              <Counter
                label="پرونده‌های باز"
                value={num(o.openCases)}
                {...(o.openCases ? { tone: 'alert' } : {})}
              />
              <Counter label="شاهد بانکی بدون تطبیق" value={num(o.openEvidence)} />
              <Counter label="برداشت در جریان" value={num(o.pendingWithdrawals)} />
            </div>
          </div>
        )}
      </Remote>
      <Remote r={runs}>
        {(d) => (
          <div className="grid gap-4 xl:grid-cols-3">
            <section className="flex flex-col gap-2 xl:col-span-2" data-recon-runs>
              <h2 className="m-0 text-[14px] font-bold">اجراهای تطبیق</h2>
              {d.runs.length === 0 ? (
                <StateView compact kind="empty" title="هنوز تطبیقی اجرا نشده است" />
              ) : (
                d.runs.map((r, i) => (
                  <details
                    key={r.id}
                    open={i === 0}
                    className="rounded-md border border-line bg-surface-1 p-3"
                    data-recon-run={r.status}
                  >
                    <summary className="flex cursor-pointer flex-wrap items-center justify-between gap-2">
                      <span className="flex items-center gap-2 text-[13px]">
                        <Tone map={RUN_STATUS} code={r.status} />
                        {r.kind === 'SCHEDULED'
                          ? 'زمان‌بندی‌شده'
                          : r.kind === 'CLI'
                            ? 'خط فرمان'
                            : 'دستی'}
                        {r.halted && <Badge tone="negative">توقف خودکار</Badge>}
                      </span>
                      <span className="text-[12px] text-fg-muted">{dt(r.startedAt)}</span>
                    </summary>
                    <ul className="m-0 mt-2 list-none divide-y divide-line p-0 text-[12.5px]">
                      {(r.checks ?? []).map((c) => (
                        <li key={c.id} className="flex items-center justify-between gap-2 py-1.5">
                          <span className="flex flex-col">
                            <span>{RECON_CHECK[c.id] ?? c.id}</span>
                            <TechCode>{c.id}</TechCode>
                          </span>
                          {c.ok ? (
                            <Badge tone="positive">سالم</Badge>
                          ) : (
                            <Badge tone="negative">{c.count.toLocaleString('fa-IR')} مغایرت</Badge>
                          )}
                        </li>
                      ))}
                    </ul>
                  </details>
                ))
              )}
            </section>
            <section className="flex flex-col gap-2" data-kill-switch-events>
              <h2 className="m-0 text-[14px] font-bold">تاریخچه‌ی توقف اضطراری</h2>
              <DataTable
                label="رویدادهای توقف"
                rows={d.killSwitchEvents}
                density="compact"
                columns={[
                  {
                    id: 'a',
                    header: 'اقدام',
                    isRowHeader: true,
                    cell: (e) => (e.action === 'HALT' ? 'توقف' : 'رفع توقف'),
                  },
                  {
                    id: 'who',
                    header: 'عامل',
                    cell: (e) => (e.actorType === 'system' ? 'سیستم (تطبیق)' : 'مدیر'),
                  },
                  { id: 'r', header: 'دلیل', cell: (e) => e.reason },
                  { id: 't', header: 'زمان', cell: (e) => dt(e.at) },
                ]}
                empty={<StateView compact kind="empty" title="رویدادی ثبت نشده است" />}
              />
            </section>
          </div>
        )}
      </Remote>
      {dialog === 'RUN' && (
        <CommandDialog
          title="اجرای تطبیق"
          reasonRequired={false}
          confirmLabel="اجرا"
          run={async () => {
            await api.post('/reconciliation/run', {});
          }}
          onClose={() => setDialog(null)}
          onDone={() => (setDialog(null), reload())}
        >
          <Facts
            rows={[
              ['اثر', 'بررسی فقط‌خواندنی دفتر کل و جریان‌ها؛ چیزی اصلاح نمی‌شود.'],
              ['در صورت مغایرت', 'عملیات مالی حساس به‌طور خودکار متوقف می‌شود.'],
            ]}
          />
        </CommandDialog>
      )}
      {(dialog === 'HALT' || dialog === 'RELEASE') && (
        <CommandDialog
          title={dialog === 'HALT' ? 'توقف اضطراری عملیات مالی' : 'رفع توقف عملیات مالی'}
          tone={dialog === 'HALT' ? 'danger' : 'positive'}
          confirmLabel={dialog === 'HALT' ? 'توقف' : 'رفع توقف'}
          run={async (reason) => {
            await api.post('/reconciliation/kill-switch', { action: dialog, reason });
          }}
          onClose={() => setDialog(null)}
          onDone={() => (setDialog(null), reload())}
          data-kill-switch-dialog={dialog}
        >
          <Facts
            rows={
              dialog === 'HALT'
                ? [
                    ['اثر', 'واریز، برداشت و اصلاح موجودی تا رفع توقف پذیرفته نمی‌شود.'],
                    ['خواندن', 'نمایش موجودی‌ها و صف‌ها ادامه دارد.'],
                  ]
                : [
                    [
                      'شرط',
                      'آخرین اجرای تطبیق باید بدون مغایرت باشد؛ در غیر این صورت سرور نمی‌پذیرد.',
                    ],
                  ]
            }
          />
        </CommandDialog>
      )}
    </AppContainer>
  );
}
